import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { RegepTechnicalResourceService } from './regep-technical-resource.service';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { generateApplicationGuidPart } from './regep-technical-resource.utils';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { ConnectionObjectInfo } from './regep-technical-resource.models';
import { RegepApplicationService } from '../../services/regep-application.service';
import { EegEnergyProducer } from '../../entities/regep-eeg-energy-producer';

@Component({
  selector: 'app-regep-technical-resource',
  templateUrl: './regep-technical-resource.component.html',
  styleUrls: ['./regep-technical-resource.component.scss'],
})
export class RegepTechnicalResourceComponent extends DestroyableComponent implements OnInit {
  @Input() isReadOnly: boolean;
  @Input() isEdit: boolean;
  @Input() applicationId: number;

  @Output() formChange = new EventEmitter<FormGroup>();

  technicalResourcesForm: FormGroup;
  isLoading: boolean = true;

  private connectionObjectInfo: ConnectionObjectInfo;

  constructor(private regepApplicationService: RegepApplicationService, public technicalResourceService: RegepTechnicalResourceService) {
    super();
  }

  ngOnInit(): void {
    this.preventLeak(this.regepApplicationService.getApplication(this.applicationId)).subscribe(
      application => {
        this.connectionObjectInfo = {
          postCode: application.connectionobject.postcode,
        };

        this.buildForm(application.eeg_energy_producers);
      },
      error => {},
      () => (this.isLoading = false)
    );
  }

  addTechnicalResource() {
    const technicalResources = this.technicalResourcesForm.controls.technicalResources as FormArray;
    technicalResources.push(this.initTechnicalResource());
    this.technicalResourcesForm.markAsDirty();
  }

  deleteTechnicalResource(index: number) {
    const technicalResources = this.technicalResourcesForm.controls.technicalResources as FormArray;
    technicalResources.removeAt(index);
  }

  isLastTechnicalResource(index: number) {
    return index === (this.technicalResourcesForm.controls.technicalResources as FormArray).length - 1;
  }

  technicalResourceUpdated(input: any, index: number) {
    this.technicalResourceService.setEegEnergyProducers(this.technicalResourcesForm.value.technicalResources);
    this.technicalResourceService.setFormState(this.technicalResourcesForm);
  }

  rowTrackByFunction(_, item: EegEnergyProducer) {
    return item.energy_producer_id;
  }

  private buildForm(eegEnergyProducers: EegEnergyProducer[]) {
    const technicalResources = [];

    if (eegEnergyProducers?.length) {
      for (const energyProducer of eegEnergyProducers) {
        technicalResources.push(
          new FormGroup({
            id: new FormControl(energyProducer.id),
            energy_producer_id: new FormControl(energyProducer.energy_producer_id),
            master_id: new FormControl(energyProducer.master_id, [Validators.required, this.masterIdValidator()]),
          })
        );
      }
    } else {
      technicalResources.push(this.initTechnicalResource());
    }

    this.technicalResourcesForm = new FormGroup({
      technicalResources: new FormArray(technicalResources, [Validators.required]),
    });

    this.onFormChange(this.technicalResourcesForm.value);

    this.preventLeak(this.technicalResourcesForm.valueChanges).subscribe(value => {
      this.onFormChange(value);
    });
  }

  private onFormChange(value) {
    this.technicalResourceService.setEegEnergyProducers(value.technicalResources);
    this.technicalResourceService.setFormState(this.technicalResourcesForm);
    this.formChange.emit(this.technicalResourcesForm);
  }

  private initTechnicalResource() {
    const technicalResources = this.technicalResourcesForm?.controls.technicalResources as FormArray;
    return new FormGroup({ energy_producer_id: new FormControl(this.generateIdentifier(technicalResources?.length ?? 0)) });
  }

  private generateIdentifier(resourceIndex: number): string {
    const clientSpecificPrefix = 'E2132201'; // TODO: extract to configuration
    const locationPart = 'DE' + this.connectionObjectInfo?.postCode || '00000';
    const applicationDynamicPart = generateApplicationGuidPart(this.applicationId, resourceIndex, { fixedLength: 8 });
    const clientSpecificSuffix = '0060000001'; // TODO: extract to configuration
    return `${clientSpecificPrefix}${locationPart}${applicationDynamicPart}${clientSpecificSuffix}`;
  }

  private masterIdValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const masterIdRegex = new RegExp('^SEE\\d{12}$');
      if (!masterIdRegex.test(control.value)) {
        return { masterIdError: true };
      }
      return null;
    };
  }
}
